// src/features/insight-v2-shared/components/MetricStamp.tsx
//
// «Coverage and freshness as a stamp, not a warning» (commercial-fit.md
// S6): one small grey line under every number, identical everywhere across
// the five w7.u5 screens (reconciliation, control, status, benchmark, fit).
// An absent cell names its remedy verbatim instead of the problem — never
// «estimado», «aproximado», «desconocido». The API states only the remedy
// code (`MetricStampDto.remedies`); the sentence lives in
// `resources.insightV2.remedies`, shared by every screen so the same code
// always reads the same sentence.
import React from 'react';
import { Typography } from '@mui/material';
import { useLocale, useTranslate } from 'react-admin';
import { DateTimeSystem } from '@/utils/dateTimeSystem';
import type { MetricStampDto } from '@/types/analyticsV2';
import { stampRemedyText } from '@/lib/v2MetricView';

/** Wire remedy code (`R-FEED`) to its i18n key (`R_FEED`). */
export const remedyKey = (code: string) => code.replace(/-/g, '_');

interface MetricStampProps {
  stamp: MetricStampDto;
  sx?: object;
}

export const MetricStamp: React.FC<MetricStampProps> = ({ stamp, sx }) => {
  const translate = useTranslate();
  const locale = useLocale();

  if (stamp.remedies.length > 0) {
    const text = stampRemedyText(stamp, translate);
    return (
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', ...sx }}>
        {text}
      </Typography>
    );
  }

  const parts: string[] = [];
  if (stamp.freshnessAsOf) {
    parts.push(translate('resources.insightV2.stamp.updated', { relative: DateTimeSystem.formatRelative(stamp.freshnessAsOf, locale) }));
  }
  if (stamp.sampleSize !== null && stamp.sampleSize !== undefined) {
    parts.push(translate('resources.insightV2.stamp.sample', { count: stamp.sampleSize }));
  }
  if (parts.length === 0) return null;

  return (
    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', ...sx }}>
      {parts.join(' · ')}
    </Typography>
  );
};

export default MetricStamp;
